import React, { useState } from "react";
import { produce } from "immer";
import useSWR, { mutate } from "swr";

import {
  Box,
  Flex,
  IconCartOutline,
  LinkButton,
  NoSSR,
  NotificationBadge,
  Span,
  Text,
} from "@catchoftheday/cg-components";
import { currency } from "@catchoftheday/utilities";

import { fetcher } from "../../../api/index";
import { HeaderTile } from "../../../components/HeaderTile";
import { ListItemPlaceHolder } from "../../../components/ListItemPlaceHolder";
import {
  HEADER_META_DATA_API,
  HEADER_MINICART_API,
  HEADER_MINICART_REMOVE_ITEM_API,
} from "../../../consts/index";
import {
  AxiosResponse,
  HeaderMetaData,
  HeaderMiniCart,
  MiniCartItem,
} from "../../../types/index";
import { getLegacyMinicart } from "../../../utilities/legacy/minicart";
import { HeaderTileContainer } from "../HeaderTileContainer";
import { ListItems } from "../ListItems";

export type MiniCartProps = {
  cartItemsAmount?: number;
};

const MINICART_ITEM_HEIGHT = "88px";
const MINICART_HEADER_HEIGHT = "125px";
const MINICART_FOOTER_HEIGHT = "40px";
const MAX_ITEMS_SHOW = 4;
const ITEM_GUTTER = "5px";
const MINICART_HEIGHT = `calc(${MINICART_ITEM_HEIGHT} * ${MAX_ITEMS_SHOW} + ${MINICART_HEADER_HEIGHT} + ${MINICART_FOOTER_HEIGHT}) + ${ITEM_GUTTER}`;
const DISABLE_MINICART_ROUTES = ["/cart", "/checkout"];

export const MiniCart: React.FC<MiniCartProps> = ({ cartItemsAmount = 0 }) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const shouldRenderMiniCartContent =
    typeof window != "undefined" &&
    !DISABLE_MINICART_ROUTES.some((path) =>
      window.location?.pathname.includes(path)
    );

  const { data: miniCartResponse, isValidating } = useSWR<
    AxiosResponse<HeaderMiniCart>
  >(
    shouldRenderMiniCartContent && shouldFetch ? HEADER_MINICART_API : null,
    fetcher
  );

  const minicartData = miniCartResponse?.data;

  const minicartItems = minicartData?.items || [];

  async function onItemRemoveHandler(item: MiniCartItem) {
    setIsLoading(true);
    try {
      // Remove cart item
      const result = await fetcher({
        url: HEADER_MINICART_REMOVE_ITEM_API,
        method: "DELETE",
        data: {
          offerId: item.offerId,
          productId: item.productId,
          attributeValueId: item.attributeValueId,
          addedByCoupon: item.addedByCoupon,
        },
      });

      const legacyMinicart = getLegacyMinicart();

      if (legacyMinicart) {
        legacyMinicart.getCart();
      }

      // Mutate Header Metadata API
      mutate(
        HEADER_META_DATA_API,
        async (currentData: AxiosResponse<HeaderMetaData>) =>
          produce(currentData, (state) => {
            state.data.cartItemCount -= item.quantity;
          })
      );

      // Mutate Header Minicart API
      mutate(HEADER_MINICART_API, result, false);
    } catch (e) {
      // TODO: Call logging API
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }

  const renderMiniCartHeader = () => (
    <Box
      p="16px"
      boxShadow="box"
      backgroundColor="white"
      color="black"
      height={MINICART_HEADER_HEIGHT}
      zIndex={1}
      data-testid="minicart-header"
    >
      <Flex justifyContent="space-between" fontSize="16px">
        <Box fontWeight="semibold">
          My Cart
          <Span fontWeight="light" pl="3px">
            {`(${cartItemsAmount} item${cartItemsAmount > 1 ? "s" : ""})`}
          </Span>
        </Box>
        <Span fontWeight="semibold" data-testid="minicart-subtotalprice">
          ${currency(minicartData?.subTotalPrice || 0).toString()}
        </Span>
      </Flex>
      <Text
        color="typography.subText"
        fontSize="xs"
        textAlign="right"
        mt={0}
        mb="10px"
      >
        Shipping calculated at checkout
      </Text>
      <LinkButton
        block
        variant="primary"
        href="/checkout/payment"
        data-tracking="header-app-mini-cart-checkout"
      >
        <Span fontWeight="semibold">checkout</Span>
      </LinkButton>
    </Box>
  );

  const renderMiniCartFooter = () => {
    return !minicartData?.hideClubCatchFreeShippingNotice ? (
      <Box
        p="8px 18px"
        bg="ui.hintColor"
        height={MINICART_FOOTER_HEIGHT}
        data-testid="minicart-footer"
      >
        <Text m={0} fontSize="2xs" lineHeight="13px">
          Please note: As a OnePass member, you&apos;re eligible for FREE
          standard delivery when you purchase eligible Catch products!
        </Text>
      </Box>
    ) : null;
  };

  const renderMiniCartContent = () => {
    const shouldRenderItems = minicartItems.length > 0;
    const shouldShowPlaceholder = isValidating && !shouldRenderItems;
    const shouldShowLoadingStatus = isLoading || isValidating;

    return (
      <Flex
        width="400px"
        flexDirection="column"
        color="typography.text"
        boxShadow="0 2px 4px 0 rgba(0,0,0,0.50)"
        bg="white"
        maxHeight={MINICART_HEIGHT}
        height={shouldShowPlaceholder ? "280px" : undefined}
        data-testid="minicart-body"
      >
        {shouldShowPlaceholder ? (
          <Box width="100%" p="16px">
            <ListItemPlaceHolder numberOfItems={3} py="13px" />
          </Box>
        ) : (
          <Box
            data-testid="minicart-container"
            css={{
              opacity: shouldShowLoadingStatus ? 0.5 : 1,
              pointerEvents: shouldShowLoadingStatus ? "none" : "auto",
            }}
          >
            {shouldRenderItems ? (
              <>
                {renderMiniCartHeader()}
                <Box
                  data-testid="minicart-body"
                  maxHeight={`calc(${MINICART_HEIGHT} - ${MINICART_HEADER_HEIGHT} - ${MINICART_FOOTER_HEIGHT})`}
                  overflowY="auto"
                >
                  <ListItems
                    items={minicartItems}
                    onItemRemove={onItemRemoveHandler}
                  />
                </Box>
                {renderMiniCartFooter()}
              </>
            ) : (
              <Box px="16px" py="24px" textAlign="center">
                <Span fontWeight="bold">
                  You have no items in your shopping cart.
                </Span>
              </Box>
            )}
          </Box>
        )}
      </Flex>
    );
  };

  return (
    <HeaderTile
      popoverProps={{
        placement: "bottom-end",
      }}
      onTileShow={(show) => setShouldFetch(show)}
      icon={({ isOpen }) => (
        <HeaderTileContainer
          data-testid="minicart-reference"
          flexProps={{
            px: [null, null, "0.7875rem", null, "1.53rem"],
          }}
          linkProps={{
            id: "mini-cart",
            backgroundColor: isOpen ? "white" : undefined,
            color: isOpen ? "secondary" : "inherit",
            href: "/cart",
            "aria-label": "Header Cart",
            className: "js-dnt",
            "data-tracking": "header-app-cart",
          }}
        >
          <Box position="relative">
            <NoSSR>
              {Boolean(cartItemsAmount) && (
                <NotificationBadge
                  text={cartItemsAmount}
                  data-testid="cart-items-amount"
                />
              )}
            </NoSSR>
            <IconCartOutline width={"34px"} height={"30px"} title="Cart" />
          </Box>
        </HeaderTileContainer>
      )}
    >
      {shouldRenderMiniCartContent && renderMiniCartContent()}
    </HeaderTile>
  );
};

export default MiniCart;
